import * as React from "react";

import { pauseLinkedinAgent, startLinkedinAgent } from "../api/linkedin-agent";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { brand } from "../lib/brand";
import { PauseAgentDialog } from "./pause-agent-dialog";
import { Switch, SwitchProps } from "./switch";
import { toaster } from "./toaster";

interface LinkedinAgentSwitchProps extends SwitchProps {
  workspace: string;
  adAccountId: number;
  campaignId: number;
  campaignName: string | null;
  on: boolean;
  onToggled: () => void | Promise<void>;
}

// Switching on starts the agent right away; switching off asks first,
// since it hands the campaign's bids and budget back.
export function LinkedinAgentSwitch({
  adAccountId,
  campaignId,
  campaignName,
  disabled,
  on,
  onToggled,
  workspace,
  ...switchProps
}: LinkedinAgentSwitchProps) {
  const withToaster = useToaster();
  const [isToggling, withToggling] = useLoading();
  const [isConfirming, setIsConfirming] = React.useState(false);

  const toggle = (
    request: typeof startLinkedinAgent,
    title: string,
    description: string,
  ) =>
    withToggling(async () => {
      await withToaster(() => request(workspace, adAccountId, campaignId));
      toaster.create({ type: "success", title, description });
      await onToggled();
    }).catch(() => {});

  const start = () =>
    toggle(
      startLinkedinAgent,
      `${brand.productName} is on`,
      "It will adjust this campaign's bids and budget.",
    );

  const pause = () => {
    setIsConfirming(false);
    toggle(
      pauseLinkedinAgent,
      `${brand.productName} is off`,
      "It will stop making changes to this campaign.",
    );
  };

  return (
    <>
      <Switch
        aria-label={brand.productName}
        checked={on}
        colorPalette="blue"
        disabled={disabled || isToggling}
        onCheckedChange={(details) => {
          if (details.checked) {
            start();
          } else {
            setIsConfirming(true);
          }
        }}
        {...switchProps}
      />
      <PauseAgentDialog
        campaignName={campaignName}
        onCancel={() => setIsConfirming(false)}
        onConfirm={pause}
        open={isConfirming}
      />
    </>
  );
}
